import React from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import MergeTypeOutlined from "@mui/icons-material/MergeTypeOutlined";
import { useVideoProject, isClipReady } from "./VideoProjectContext";
import ClipThumbnail, { AddClipTile } from "./ClipThumbnail";
import StitchModal from "./StitchModal";
import SelectVideosModal from "./SelectVideosModal";
import { MIN_STITCH_CLIPS, MAX_STITCH_DURATION_SEC, MAX_PROJECT_CLIPS } from "./videoProjectConstants";

export default function ProjectPanel({
  lang,
  currentOutputJob,
  selectOpen: selectOpenProp,
  onSelectOpen,
  onSelectClose,
  segmentProgress,
  onRegenerateSegment,
  regenerateBusy = false,
  onGenerateSegment,
  onRemoveClip,
}) {
  const theme = useTheme();
  const { clips, readyClips, totalDuration, removeClip, reorderClips, maxClips } = useVideoProject();

  const handleRemove = (clip) => {
    if (typeof onRemoveClip === "function") {
      onRemoveClip(clip);
      return;
    }
    removeClip(clip.id);
  };
  const [selectOpenLocal, setSelectOpenLocal] = React.useState(false);
  const selectOpen = selectOpenProp ?? selectOpenLocal;
  const closeSelect = onSelectClose || (() => setSelectOpenLocal(false));
  const openSelect = onSelectOpen || (() => setSelectOpenLocal(true));
  const [stitchOpen, setStitchOpen] = React.useState(false);
  const [dragFrom, setDragFrom] = React.useState(null);
  const [dragOver, setDragOver] = React.useState(null);

  const handleDrop = (toIndex) => {
    if (dragFrom == null || dragFrom === toIndex) {
      setDragFrom(null);
      setDragOver(null);
      return;
    }
    reorderClips(dragFrom, toIndex);
    setDragFrom(null);
    setDragOver(null);
  };

  const readyCount = readyClips?.length ?? clips.filter((c) => isClipReady(c)).length;
  const readyDuration = (readyClips || clips.filter((c) => isClipReady(c))).reduce(
    (sum, c) => sum + (Number(c.duration) || 0),
    0,
  );
  const canStitch =
    readyCount >= MIN_STITCH_CLIPS && readyDuration <= MAX_STITCH_DURATION_SEC;
  const atMax = clips.length >= maxClips;

  const anyGenerating = clips.some((c) => c.status === "generating");
  const stitchSubtitle =
    segmentProgress?.phase === "stitching" || segmentProgress?.phase === "voiceover"
      ? lang?.videoStitchJoiningSubtitle ||
        "Joining ready scenes into one final video…"
      : anyGenerating
        ? lang?.videoStitchGeneratingSubtitle ||
          "Scenes appear here as they finish — join starts automatically when all are ready."
        : clips.some((c) => c.status === "failed" || c.status === "pending")
          ? lang?.videoStitchPendingSubtitle ||
            "Generate each scene (or run all), then Join videos — or wait for auto-join."
          : lang?.videoStitchSubtitle ||
            "Add clips from your library, drag to reorder, then join into one final video.";

  const clipSummary =
    clips.length === 0
      ? lang?.videoStitchEmptyShort || "Add clips, drag to reorder"
      : (lang?.videoStitchTotalShort || "{duration}s · {count} clips")
          .replace("{duration}", String(totalDuration))
          .replace("{count}", String(clips.length));

  return (
    <>
      <Box
        data-tour="video-studio-clip-board"
        sx={{
          position: "fixed",
          bottom: 0,
          left: { xs: 0, md: "240px" },
          right: 0,
          zIndex: (t) => t.zIndex.drawer + 1,
          px: { xs: 1, sm: 2 },
          pb: { xs: 1, sm: 1.5 },
          pt: 0.5,
          pointerEvents: "none",
          "& > *": { pointerEvents: "auto" },
        }}
      >
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderRadius: 2,
            bgcolor: theme.palette.background.paper,
            boxShadow: `0 -4px 24px ${alpha(theme.palette.common.black, 0.12)}`,
            border: "0.5px solid transparent",
            backgroundImage: `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}), linear-gradient(90deg, #A78BFA, #EC4899, #F97316)`,
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "stretch", md: "center" },
              gap: { xs: 1.5, md: 2 },
            }}
          >
            {/* Left — same visual language as Video Studio PageHeader */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.5, sm: 2 },
                flexShrink: 0,
                minWidth: { md: 280 },
                maxWidth: { md: 360 },
              }}
            >
              <Box
                sx={{
                  width: { xs: 44, sm: 50 },
                  height: { xs: 44, sm: 50 },
                  borderRadius: 2,
                  flexShrink: 0,
                  background: "linear-gradient(135deg, #A78BFA 0%, #EC4899 50%, #F97316 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                }}
              >
                <MergeTypeOutlined sx={{ fontSize: { xs: 24, sm: 28 } }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  {lang?.videoStitchTitle || "Clip board"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: { xs: "0.813rem", sm: "0.875rem" },
                    mt: 0.25,
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  {stitchSubtitle}
                </Typography>
              </Box>
            </Box>

            {/* Center — clip queue */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{
                flex: 1,
                minWidth: 0,
                overflowX: "auto",
                py: 0.5,
                px: { xs: 0, md: 0.5 },
              }}
            >
              {clips.map((clip, index) => (
                <ClipThumbnail
                  key={clip.id}
                  clip={clip}
                  index={index}
                  onRemove={
                    clip.status === "generating"
                      ? undefined
                      : () => handleRemove(clip)
                  }
                  onRegenerate={
                    onGenerateSegment || onRegenerateSegment
                      ? (c) =>
                          (onGenerateSegment || onRegenerateSegment)(Number(c.segmentIndex))
                      : undefined
                  }
                  regenerateDisabled={regenerateBusy && clip.status === "generating"}
                  onDragStart={isClipReady(clip) ? setDragFrom : undefined}
                  onDragOver={
                    isClipReady(clip) ? (_, idx) => setDragOver(idx) : undefined
                  }
                  onDrop={isClipReady(clip) ? handleDrop : undefined}
                  isDragOver={dragOver === index && dragFrom !== index}
                  overlap={index > 0}
                />
              ))}

              <AddClipTile onClick={openSelect} disabled={atMax} lang={lang} />
            </Stack>

            {/* Right — actions */}
            <Stack
              justifyContent="center"
              alignItems={{ xs: "stretch", md: "flex-end" }}
              spacing={0.75}
              sx={{ flexShrink: 0, minWidth: { md: 140 } }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                textAlign={{ xs: "left", md: "right" }}
              >
                {clipSummary}
              </Typography>
              {totalDuration > MAX_STITCH_DURATION_SEC ? (
                <Typography
                  variant="caption"
                  color="error.main"
                  fontWeight={700}
                  textAlign={{ xs: "left", md: "right" }}
                >
                  {(lang?.videoProjectDurationCap || "Max {max}s total").replace(
                    "{max}",
                    String(MAX_STITCH_DURATION_SEC),
                  )}
                </Typography>
              ) : null}
              <Button
                variant="contained"
                size="medium"
                startIcon={<MergeTypeOutlined />}
                disabled={!canStitch}
                onClick={() => setStitchOpen(true)}
                sx={{
                  fontWeight: 900,
                  whiteSpace: "nowrap",
                  background: "linear-gradient(135deg, #A78BFA 0%, #EC4899 50%, #F97316 100%)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #9f7aea 0%, #db2777 50%, #ea580c 100%)",
                  },
                }}
              >
                {lang?.videoStitchJoin || "Join videos"}
              </Button>
              <Typography
                variant="caption"
                color="text.disabled"
                textAlign={{ xs: "left", md: "right" }}
              >
                {readyCount < MIN_STITCH_CLIPS
                  ? lang?.videoStitchMinClips || `Need ${MIN_STITCH_CLIPS}+ clips`
                  : `~${readyDuration}s`}
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Box>

      <SelectVideosModal
        open={selectOpen}
        onClose={closeSelect}
        lang={lang}
        extraJob={
          currentOutputJob?.video_url
            ? {
                id: currentOutputJob.jobId,
                video_url: currentOutputJob.videoUrl,
                ui_model: currentOutputJob.modelId,
                duration_sec: currentOutputJob.duration,
                prompt: currentOutputJob.prompt,
                resolution: currentOutputJob.resolution,
                status: "completed",
              }
            : null
        }
      />

      <StitchModal
        open={stitchOpen}
        onClose={() => setStitchOpen(false)}
        clips={(readyClips || clips).filter((c) => isClipReady(c))}
        totalDuration={readyDuration}
        lang={lang}
      />
    </>
  );
}
